/**
 * ============================================================
 * MANORRA — Core JS Library
 * ============================================================
 * Single bundle for all interactive behaviors on Lyseane theme.
 * Loaded once per page, modules auto-init on DOMContentLoaded.
 *
 * Modules included:
 *   - Reveal (IntersectionObserver scroll animations)
 *   - StickyBar (show/hide based on hero visibility)
 *   - Gallery (slides, dots, thumbs, arrows, swipe)
 *   - QtySelector (+/-)
 *   - VariantPicker (option click → update variant, price, ATC)
 *   - AddToCart (fetch /cart/add.js + drawer integration)
 *
 * Usage in section/layout:
 *   {{ 'lyseane-core.js' | asset_url | script_tag }}
 *
 * All modules namespaced under window.Lyseane.
 * ============================================================
 */

(function () {
  'use strict';

  // ===== Namespace =====
  const Lyseane = window.Lyseane = window.Lyseane || {};

  // ===== Utils =====
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);
  const debounce = (fn, wait = 100) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
  };
  const formatMoney = (cents, currency = '€') => {
    const n = (cents / 100).toFixed(2).replace('.', ',');
    return n.endsWith(',00') ? n.slice(0, -3) + ' ' + currency : n + ' ' + currency;
  };
  const emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(`lyseane:${name}`, { detail, bubbles: true }));
  };

  Lyseane.utils = { $, $$, on, debounce, formatMoney, emit };

  // ============================================================
  // MODULE 1 : Reveal (scroll-triggered animations)
  // ============================================================
  Lyseane.reveal = {
    init() {
      const elements = $$('.ly-reveal, [data-lyseane-reveal]');
      if (!elements.length) return;
      if (!('IntersectionObserver' in window)) {
        // Fallback: show all immediately
        elements.forEach(el => el.classList.add('is-visible'));
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, {
        rootMargin: '0px 0px 240px 0px',
        threshold: 0
      });
      elements.forEach(el => observer.observe(el));
    }
  };

  // ============================================================
  // MODULE 2 : Sticky Bar (show when hero exits viewport)
  // ============================================================
  Lyseane.stickyBar = {
    init() {
      const bar = $('[data-lyseane-sticky-bar]');
      if (!bar) return;

      // Find the trigger element (the buybox / hero area to hide bar when visible)
      const trigger = $('[data-lyseane-sticky-trigger]') || $('.myo .buybox') || $('.lyseane .ly-buybox') || $('[data-lyseane-buybox]');

      if (!trigger) {
        // No trigger → show bar always after 600px scroll
        const onScroll = () => {
          if (window.scrollY > 600) {
            bar.classList.add('is-visible');
            bar.setAttribute('aria-hidden', 'false');
          } else {
            bar.classList.remove('is-visible');
            bar.setAttribute('aria-hidden', 'true');
          }
        };
        on(window, 'scroll', debounce(onScroll, 50));
        onScroll();
        return;
      }

      // With trigger: show bar when trigger is OUT of viewport
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            bar.classList.remove('is-visible');
            bar.setAttribute('aria-hidden', 'true');
          } else {
            bar.classList.add('is-visible');
            bar.setAttribute('aria-hidden', 'false');
          }
        });
      }, { threshold: 0, rootMargin: '0px 0px -100px 0px' });
      observer.observe(trigger);

      // ATC button on sticky bar
      const stickyAtc = $('[data-lyseane-sticky-atc]', bar);
      if (stickyAtc) {
        on(stickyAtc, 'click', async (e) => {
          e.preventDefault();
          const variantId = stickyAtc.dataset.variantId;
          if (!variantId) return;
          const qty = parseInt($('[data-lyseane-sticky-qty-wrap] input')?.value || '1', 10);
          await Lyseane.cart.add(variantId, qty, stickyAtc);
        });
      }
    }
  };

  // ============================================================
  // MODULE 3 : Gallery (slides + dots + thumbs + arrows)
  // ============================================================
  Lyseane.gallery = {
    init() {
      $$('[data-lyseane-gallery]').forEach(gallery => this.bind(gallery));
    },
    bind(gallery) {
      const slides = $$('[data-lyseane-gallery-slide]', gallery);
      const dots = $$('[data-lyseane-gallery-dot]', gallery);
      const thumbs = $$('[data-lyseane-gallery-thumb]', gallery);
      const prev = $('[data-lyseane-gallery-prev]', gallery);
      const next = $('[data-lyseane-gallery-next]', gallery);
      if (!slides.length) return;
      let current = 0;

      const goto = (idx) => {
        current = ((idx % slides.length) + slides.length) % slides.length;
        slides.forEach((s, i) => s.classList.toggle('is-active', i === current));
        dots.forEach((d, i) => {
          d.classList.toggle('is-active', i === current);
          d.setAttribute('aria-selected', i === current ? 'true' : 'false');
        });
        thumbs.forEach((t, i) => t.classList.toggle('is-active', i === current));
      };

      dots.forEach((d, i) => on(d, 'click', () => goto(i)));
      thumbs.forEach((t, i) => on(t, 'click', () => goto(i)));
      on(prev, 'click', () => goto(current - 1));
      on(next, 'click', () => goto(current + 1));

      // Keyboard nav
      on(gallery, 'keydown', (e) => {
        if (e.key === 'ArrowLeft') goto(current - 1);
        if (e.key === 'ArrowRight') goto(current + 1);
      });

      // Touch swipe
      let startX = 0;
      const stage = $('.ly-gallery-stage', gallery);
      if (stage) {
        on(stage, 'touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
        on(stage, 'touchend', (e) => {
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 50) goto(current + (dx < 0 ? 1 : -1));
        }, { passive: true });
      }

      gallery._lyGoto = goto;
    }
  };

  // ============================================================
  // MODULE 4 : QtySelector
  // ============================================================
  Lyseane.qty = {
    init() {
      $$('[data-lyseane-qty]').forEach(wrap => this.bind(wrap));
    },
    bind(wrap) {
      const input = $('[data-lyseane-qty-input]', wrap);
      const dec = $('[data-lyseane-qty-decrease]', wrap);
      const inc = $('[data-lyseane-qty-increase]', wrap);
      if (!input) return;

      const min = parseInt(input.min || '1', 10);
      const max = parseInt(input.max || '99', 10);

      const setVal = (v) => {
        v = Math.max(min, Math.min(max, v));
        input.value = v;
        dec.disabled = v <= min;
        inc.disabled = v >= max;
        emit('qty-change', { value: v, wrap });
      };

      on(dec, 'click', () => setVal(parseInt(input.value, 10) - 1));
      on(inc, 'click', () => setVal(parseInt(input.value, 10) + 1));
      on(input, 'change', () => setVal(parseInt(input.value, 10) || min));
      setVal(parseInt(input.value, 10) || min);
    }
  };

  // ============================================================
  // MODULE 5 : VariantPicker
  // ============================================================
  Lyseane.variants = {
    init() {
      $$('[data-lyseane-variant-picker]').forEach(picker => this.bind(picker));
    },
    bind(picker) {
      const productId = picker.dataset.productId;
      const dataScript = picker.parentNode.querySelector('[data-lyseane-product-variants]') || $('[data-lyseane-product-variants]');
      if (!dataScript) return;

      let variants = [];
      try { variants = JSON.parse(dataScript.textContent); } catch (e) {}

      const groups = $$('[data-lyseane-variant-group]', picker);
      const hiddenInput = $('[data-lyseane-variant-id]', picker);
      const selectedOptions = [];

      // Initialize from current data-active
      groups.forEach((group, i) => {
        const active = $('[data-lyseane-variant-value].is-active', group) || $('[data-lyseane-variant-select]', group);
        selectedOptions[i] = active?.dataset?.value || active?.value || null;
      });

      const findVariant = () => {
        return variants.find(v => v.options.every((opt, i) => opt === selectedOptions[i]));
      };

      const updateState = () => {
        const v = findVariant();
        if (!v) return;
        if (hiddenInput) hiddenInput.value = v.id;
        groups.forEach((group, i) => {
          const label = $('[data-lyseane-variant-selected-value]', group);
          if (label) label.textContent = selectedOptions[i];
        });
        emit('variant-change', { variant: v, productId });
        // Update ATC button data
        $$('[data-lyseane-atc], [data-lyseane-sticky-atc]').forEach(btn => {
          btn.dataset.variantId = v.id;
          btn.disabled = !v.available;
          const label = $('[data-lyseane-atc-label]', btn);
          if (label) {
            label.textContent = v.available ? (label.dataset.original || label.textContent) : 'Indisponible';
            if (!label.dataset.original && v.available) label.dataset.original = label.textContent;
          }
        });
        // Update price displays
        $$('[data-price-now]').forEach(el => el.textContent = formatMoney(v.price));
        $$('[data-price-compare]').forEach(el => {
          if (v.compare_at_price && v.compare_at_price > v.price) {
            el.textContent = formatMoney(v.compare_at_price);
            el.style.display = '';
          } else {
            el.style.display = 'none';
          }
        });
      };

      // Pills click
      $$('[data-lyseane-variant-value]', picker).forEach(btn => {
        on(btn, 'click', () => {
          const idx = parseInt(btn.dataset.optionIndex, 10);
          const value = btn.dataset.value;
          selectedOptions[idx] = value;
          // Update active state
          const group = btn.closest('[data-lyseane-variant-group]');
          $$('[data-lyseane-variant-value]', group).forEach(b => {
            const isMe = b === btn;
            b.classList.toggle('is-active', isMe);
            b.setAttribute('aria-checked', isMe ? 'true' : 'false');
          });
          updateState();
        });
      });

      // Selects change
      $$('[data-lyseane-variant-select]', picker).forEach(sel => {
        on(sel, 'change', () => {
          const idx = parseInt(sel.dataset.optionIndex, 10);
          selectedOptions[idx] = sel.value;
          updateState();
        });
      });

      updateState();
    }
  };

  // ============================================================
  // MODULE 6 : Cart (AJAX add to cart)
  // ============================================================
  Lyseane.cart = {
    init() {
      $$('[data-lyseane-atc]').forEach(btn => this.bind(btn));
    },
    bind(btn) {
      on(btn, 'click', async (e) => {
        // If inside a <form>, let the form handler take it OR intercept
        const form = btn.closest('form');
        if (form) {
          e.preventDefault();
          const fd = new FormData(form);
          const variantId = fd.get('id') || btn.dataset.variantId;
          const qty = parseInt(fd.get('quantity') || '1', 10);
          await this.add(variantId, qty, btn);
        } else {
          e.preventDefault();
          const variantId = btn.dataset.variantId;
          const qty = parseInt($('[data-lyseane-qty-input]')?.value || '1', 10);
          await this.add(variantId, qty, btn);
        }
      });
    },
    async add(variantId, quantity, btn) {
      if (!variantId) return;
      const spinner = btn?.querySelector('[data-lyseane-atc-spinner]');
      const label = btn?.querySelector('[data-lyseane-atc-label]');
      const drawer = document.querySelector('cart-drawer');
      try {
        if (spinner) spinner.hidden = false;
        if (btn) btn.disabled = true;
        const payload = { items: [{ id: variantId, quantity }] };
        // Le drawer Shrine ne se rafraîchit que si /cart/add.js renvoie le HTML
        // des sections qu'il déclare : sans ça il s'ouvre vide et périmé.
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          payload.sections = drawer.getSectionsToRender().map(s => s.id);
          payload.sections_url = window.location.pathname;
        }
        const resp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!resp.ok) throw new Error('Erreur ajout panier');
        const item = await resp.json();
        emit('cart-added', { item });
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(item);
        } else {
          window.location.href = '/cart';
          return;
        }
        // Feedback success on button
        if (label) {
          const original = label.textContent;
          label.textContent = '✓ Ajouté';
          setTimeout(() => { label.textContent = original; }, 1500);
        }
      } catch (err) {
        console.error('[Lyseane] ATC error:', err);
        emit('cart-error', { error: err });
        if (label) {
          const original = label.textContent;
          label.textContent = 'Erreur';
          setTimeout(() => { label.textContent = original; }, 1500);
        }
      } finally {
        if (spinner) spinner.hidden = true;
        if (btn) btn.disabled = false;
      }
    }
  };

  // ============================================================
  // INIT (DOMContentLoaded or now if already loaded)
  // ============================================================
  const initAll = () => {
    try { Lyseane.reveal.init(); } catch (e) { console.warn('[Lyseane.reveal]', e); }
    try { Lyseane.stickyBar.init(); } catch (e) { console.warn('[Lyseane.stickyBar]', e); }
    try { Lyseane.gallery.init(); } catch (e) { console.warn('[Lyseane.gallery]', e); }
    try { Lyseane.qty.init(); } catch (e) { console.warn('[Lyseane.qty]', e); }
    try { Lyseane.variants.init(); } catch (e) { console.warn('[Lyseane.variants]', e); }
    try { Lyseane.cart.init(); } catch (e) { console.warn('[Lyseane.cart]', e); }
    emit('ready');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init on Shopify section reload (theme editor)
  document.addEventListener('shopify:section:load', initAll);
})();
